// Prompts Library — WP plugin parity (Screen 11 + E8).
// Dark theme, gold accent, category tabs, search, sort, copy-to-clipboard.
// Same component for both admin and employee.

import React, { useMemo, useState } from "react";
import { useShellHeader } from "../../Components/AppShell";
import { PROMPTS, CATEGORIES } from "./promptsData";

const DARK_BG     = "#0A0A0F";
const CARD_BG     = "#111118";
const BORDER      = "rgba(201, 168, 76, 0.15)";
const GOLD        = "#C9A84C";
const TEXT_DIM    = "#9CA3AF";
const TEXT_BRIGHT = "#F4EFE6";

const DIFFICULTY = {
  beginner:     { label: "BEGINNER",     bg: "#1f3d2d", fg: "#86efac" },
  intermediate: { label: "INTERMEDIATE", bg: "#3d3320", fg: "#fcd34d" },
  advanced:     { label: "ADVANCED",     bg: "#3d1f20", fg: "#fca5a5" },
};

export default function PromptsPage() {
  useShellHeader({ title: "Prompts Library" }, []);
  const [active, setActive] = useState("__all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("default");
  const [copiedId, setCopiedId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const counts = useMemo(() => {
    const m = { __all: PROMPTS.length };
    for (const c of CATEGORIES) m[c.id] = PROMPTS.filter((p) => p.category === c.id).length;
    return m;
  }, []);

  const filtered = useMemo(() => {
    let rows = PROMPTS;
    if (active !== "__all") rows = rows.filter((p) => p.category === active);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((p) =>
      p.title.toLowerCase().includes(q) ||
      p.prompt.toLowerCase().includes(q) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(q))
    );
    if (sort === "az") rows = [...rows].sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "za") rows = [...rows].sort((a, b) => b.title.localeCompare(a.title));
    return rows;
  }, [active, query, sort]);

  async function copy(p) {
    try {
      await navigator.clipboard.writeText(p.prompt);
      setCopiedId(p.id);
      setTimeout(() => setCopiedId((id) => (id === p.id ? null : id)), 1500);
    } catch { /* ignore */ }
  }

  return (
    <div style={{ background: DARK_BG, color: TEXT_BRIGHT, padding: 0, borderRadius: 12, overflow: "hidden", border: `1px solid ${BORDER}` }}>
      {/* Hero */}
      <div style={{
        padding: "40px 32px 28px",
        background: "linear-gradient(180deg, rgba(201,168,76,0.06) 0%, rgba(0,0,0,0) 100%)",
      }}>
        <div style={{ fontSize: 11, color: GOLD, letterSpacing: "0.2em", fontWeight: 700, textTransform: "uppercase" }}>
          Content Tools — Team Access
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 38, fontWeight: 700, margin: "8px 0 6px",
          color: TEXT_BRIGHT, letterSpacing: "-0.02em",
        }}>
          ✨ Prompts Library
        </h1>
        <div style={{ fontSize: 14, color: TEXT_DIM, marginBottom: 16 }}>
          {PROMPTS.length} AI prompts across {CATEGORIES.length} categories. Find the right prompt and copy it directly to your clipboard.
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="🔍 Search by title, tag or keyword..."
          style={{
            width: "100%", maxWidth: 720,
            background: "rgba(20,20,28,0.8)", color: TEXT_BRIGHT,
            border: `1px solid ${BORDER}`, borderRadius: 10,
            padding: "12px 16px", fontSize: 14, outline: "none",
          }}
        />
        <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={{
            background: "rgba(20,20,28,0.8)", color: TEXT_BRIGHT,
            border: `1px solid ${BORDER}`, borderRadius: 8,
            padding: "6px 10px", fontSize: 12,
          }}>
            <option value="default">Default order</option>
            <option value="az">Title (A–Z)</option>
            <option value="za">Title (Z–A)</option>
          </select>
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ padding: "0 32px 8px", display: "flex", flexWrap: "wrap", gap: 6 }}>
        <CatTab active={active === "__all"} onClick={() => setActive("__all")} label={`+ All ${counts.__all}`} />
        {CATEGORIES.map((c) => (
          <CatTab key={c.id} active={active === c.id} onClick={() => setActive(c.id)} label={`${c.icon} ${c.label} ${counts[c.id] || 0}`} />
        ))}
      </div>

      {/* Count */}
      <div style={{ padding: "8px 32px 16px", fontSize: 12, color: TEXT_DIM }}>{filtered.length} prompts</div>

      {/* Grid */}
      <div style={{ padding: "0 32px 40px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {filtered.map((p) => {
          const cat = CATEGORIES.find((c) => c.id === p.category);
          const diff = DIFFICULTY[p.difficulty] || DIFFICULTY.beginner;
          const isExp = !!expanded[p.id];
          const isCopied = copiedId === p.id;
          return (
            <div key={p.id} style={{
              background: CARD_BG, border: `1px solid ${BORDER}`,
              borderRadius: 12, padding: 18, display: "flex", flexDirection: "column", gap: 10,
            }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ background: "rgba(201,168,76,0.1)", color: GOLD, padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                  {cat ? `${cat.icon} ${cat.label}` : p.category}
                </span>
                <span style={{ background: diff.bg, color: diff.fg, padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em" }}>
                  {diff.label}
                </span>
              </div>
              <div style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 18, fontWeight: 700, color: GOLD, lineHeight: 1.25,
              }}>{p.title}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(p.tags || []).map((t) => (
                  <span key={t} style={{ background: "rgba(255,255,255,0.04)", color: TEXT_DIM, padding: "1px 7px", borderRadius: 6, fontSize: 10 }}>#{t}</span>
                ))}
              </div>
              <pre style={{
                margin: 0, fontFamily: "'DM Mono', ui-monospace, monospace",
                fontSize: 11, lineHeight: 1.6, color: TEXT_DIM,
                background: "rgba(0,0,0,0.3)", padding: 10, borderRadius: 6,
                whiteSpace: "pre-wrap", wordBreak: "break-word",
                maxHeight: isExp ? "none" : 120, overflow: "hidden",
              }}>{p.prompt}</pre>
              <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                <button
                  onClick={() => setExpanded({ ...expanded, [p.id]: !isExp })}
                  style={{
                    flex: 1, background: "rgba(255,255,255,0.04)", color: TEXT_DIM,
                    border: `1px solid ${BORDER}`, padding: "8px 12px",
                    borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer",
                  }}
                >{isExp ? "↑ Collapse" : "↓ Expand"}</button>
                <button
                  onClick={() => copy(p)}
                  style={{
                    background: isCopied ? "#10B981" : GOLD,
                    color: isCopied ? "white" : "#1a1611",
                    border: "none", padding: "8px 16px",
                    borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
                    minWidth: 80,
                  }}
                >{isCopied ? "✓ Copied!" : "Copy"}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CatTab({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? "rgba(201,168,76,0.15)" : "rgba(255,255,255,0.03)",
        color: active ? GOLD : TEXT_DIM,
        border: active ? `1px solid ${GOLD}` : `1px solid ${BORDER}`,
        padding: "5px 12px", borderRadius: 14, fontSize: 12, fontWeight: 600,
        cursor: "pointer", whiteSpace: "nowrap",
      }}
    >{label}</button>
  );
}
